import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Checkbox, Form } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { CheckboxChangeEventTarget } from 'antd/lib/checkbox/Checkbox';
import { v4 } from 'uuid';
import {
  getCondoBookPackageWithCard,
  ICondoReviewBookState,
  ICouponState,
  IMenuState,
  setCondoBookingComplete,
  setCondoGuest,
  setCondoPriceChangedError,
  setErrorMessageExpirationDate,
  setErrorMessageCardNumber,
  setErrorMessageZipCode,
  getNavigationMenu,
} from 'src/store/slices/index';
import { AppThunk, RootState } from 'src/store';
import {
  getAvailabilityId,
  getCondoId,
  getMemberPhone,
  getSessionObj,
  removingDashFromString,
  renderNumber,
  updateCondoGuest,
  UrlUtils,
  getFullUrl,
} from 'src/utils/index';
import { BlueButton } from 'src/components/components/index';
import {
  CONDO_SESSION_KEY,
  CONDO_UNITS_SESSION_KEY_LABEL,
  Routes,
  countriesCode,
} from 'src/constants/index';
import {
  BookingErrorsEnum,
  ICondoBookPackageWithCard,
  ICondoGuestBooking,
  ISessionKey,
  IFormErrors,
  ScrollBehaviorEnum,
} from 'src/common-types/index';
import { CondoGuestInfo } from '../condo-guest-info';
import { CondoPaymentMethod } from '../condo-payment-method';
import { CondoConfirmationEmail } from '../condo-confirmation-email';
import { CondoCancellationPolicies } from '../condo-cancellation-policies';
import { BookingConfirmed, ReservationProgress } from '../../notification';
import { CondoBookingFailedWarning } from '../../condo-booking-failed-warning';
import { CondoBookingPendingNotification } from '../booking-pending-notification';
import { CondoUnitNotAvailableNotification } from '../condo-unit-not-available';
import './style.scss';

interface IMapStateToProps {
  condoReviewBookStore: ICondoReviewBookState;
  redeemCodeStore: ICouponState;
  menuStore: IMenuState;
}

interface IMapDispatchToProps {
  getCondoBookPackageWithCard: (bookWithCard: ICondoBookPackageWithCard) => void;
  setCondoGuest: (guest: ICondoGuestBooking) => void;
  setCondoPriceChangedError: (priceChanged: boolean) => void;
  setCondoBookingComplete: (bookingComplete: boolean) => void;
  setErrorMessageExpirationDate: (value: boolean) => void;
  setErrorMessageCardNumber: (value: boolean) => void;
  setErrorMessageZipCode: (value: boolean) => void;
  getNavigationMenu: () => AppThunk;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps {}

interface IState {
  isAccept: boolean;
  isAcceptKnowBeforeYouGo: boolean;
  isSomeoneElse: boolean;
}

const ZERO = 0;
const SECOND_ELEMENT = 2;
const TEN_SEC = 10000;
const PRIVACY = 'privacy1';

class CondoReviewBookComponent extends React.Component<IProps, IState> {
  state: IState = {
    isAccept: false,
    isAcceptKnowBeforeYouGo: false,
    isSomeoneElse: false,
  };
  formRef = React.createRef<FormInstance>();

  onChangeAccept = (e: { target: CheckboxChangeEventTarget }): void => {
    this.setState({ isAccept: e.target.checked });
  };

  onChangeAcceptKnowBeforeYouGo = (e: { target: CheckboxChangeEventTarget }): void => {
    this.setState({ isAcceptKnowBeforeYouGo: e.target.checked });
  };

  onCompleteBooking = () => {
    const { condoBookingSummary, card, guest } = this.props.condoReviewBookStore;
    const { coupon } = this.props.redeemCodeStore;
    const values = UrlUtils.getValues();
    const sessionKey = getSessionObj(
      values[CONDO_UNITS_SESSION_KEY_LABEL] as ISessionKey,
      values[CONDO_SESSION_KEY] as ISessionKey,
    );

    const data = {
      sessionKey,
      bookingPrice: condoBookingSummary.bookingCard.bookingPrice.payNowPrice,
      condoId: getCondoId(),
      availabilityId: getAvailabilityId(),
      card,
      couponCode: coupon,
      guests: [{ ...guest, phone: removingDashFromString(guest.phone) }],
      supplierType: condoBookingSummary.supplierType,
    };

    this.formRef.current.setFieldsValue({ email: guest.email });
    this.formRef.current.setFieldsValue({ givenName: guest.givenName });
    this.formRef.current.setFieldsValue({ surname: guest.surname });
    this.formRef.current.setFieldsValue({ phone: guest.phone });

    Promise.all([this.formRef.current.validateFields()])
      .then(() => {
        this.props.setErrorMessageExpirationDate(true);
        this.props.setErrorMessageCardNumber(true);
        this.props.setErrorMessageZipCode(true);
        this.props.getCondoBookPackageWithCard(data);
      })
      .catch((error: IFormErrors) => {
        const { errorFields } = error;
        const { name } = errorFields[ZERO];
        const { scrollToField } = this.formRef.current;

        (() => {
          scrollToField(name[ZERO], { behavior: ScrollBehaviorEnum.Smooth });
        })();
      });
  };

  onChangeSomeoneElse = (e: { target: CheckboxChangeEventTarget }) => {
    const { memberType } = this.props.menuStore.items;
    const { guest, condoBookingSummary } = this.props.condoReviewBookStore;
    const { givenName, surname } = condoBookingSummary.mainGuest;

    if (!e.target.checked) {
      this.formRef.current.setFieldsValue({ givenName: givenName });
      this.formRef.current.setFieldsValue({ surname: surname });
      this.formRef.current.setFieldsValue({
        phone: getMemberPhone(memberType, condoBookingSummary.mainGuest.phone),
      });
    }

    this.setState({ isSomeoneElse: e.target.checked });
    this.props.setCondoGuest({
      ...(updateCondoGuest(guest, {
        isBookingForSomeoneElse: e.target.checked,
        givenName: !e.target.checked ? givenName : '',
        surname: !e.target.checked ? surname : '',
        phone: !e.target.checked
          ? getMemberPhone(memberType, condoBookingSummary.mainGuest.phone)
          : '',
      }) as ICondoGuestBooking),
      isBookingForSomeoneElse: e.target.checked,
    });
  };

  componentDidMount() {
    const { items } = this.props.menuStore;
    const { givenName, surname, email, phone, namePrefix } =
      this.props.condoReviewBookStore.condoBookingSummary.mainGuest;

    this.props.setCondoGuest({
      id: v4(),
      givenName: givenName ? givenName : '',
      namePrefix: namePrefix ? namePrefix : '',
      surname: surname ? surname : '',
      countryCode: '',
      phoneCountryCode: countriesCode[ZERO].dialCode,
      phone:
        items && getMemberPhone(items.memberType, phone)
          ? getMemberPhone(items.memberType, phone)
          : '',
      email: email ? email : '',
      additionalEmail: '',
      isBookingForSomeoneElse: false,
      addressLine1: '',
      addressLine2: '',
      city: '',
      country: '',
      stateCode: '',
      zipCode: '',
    });

    this.props.setCondoBookingComplete(false);
  }

  getBookingErrorWarning = (): React.ReactNode | null => {
    const { bookingErrorCode } = this.props.condoReviewBookStore;

    if (bookingErrorCode === BookingErrorsEnum.PaymentFailed) {
      return (
        <CondoBookingFailedWarning
          header="payment.authorize.failed"
          description="check.card.details.or.use.another.payment.method"
        />
      );
    } else if (bookingErrorCode === BookingErrorsEnum.GuestInformationFailed) {
      return (
        <CondoBookingFailedWarning
          header="issue.with.guest.information"
          description="check.you.entered.valid.guest.information"
        />
      );
    } else if (bookingErrorCode === BookingErrorsEnum.UnexpectedResponse) {
      return (
        <CondoBookingFailedWarning
          header="something.went.wrong"
          description="there.may.be.an.issue"
        />
      );
    }

    return null;
  };

  componentDidUpdate(prevProps: Readonly<IProps>) {
    const { bookingComplete, errorBooking } = this.props.condoReviewBookStore;

    if (
      !prevProps.condoReviewBookStore?.bookingErrorCode ||
      prevProps.condoReviewBookStore?.bookingErrorCode !==
        this.props.condoReviewBookStore?.bookingErrorCode
    ) {
      if (this.props.condoReviewBookStore.bookingErrorCode === BookingErrorsEnum.PaymentFailed) {
        this.formRef.current.setFields([
          { name: 'holderName', errors: [''] },
          { name: 'cardNumber', errors: [''] },
          { name: 'months', errors: [''] },
          { name: 'years', errors: [''] },
          { name: 'code', errors: [''] },
        ]);
      } else if (
        this.props.condoReviewBookStore.bookingErrorCode ===
        BookingErrorsEnum.GuestInformationFailed
      ) {
        this.formRef.current.setFields([
          { name: 'prefixName', errors: [''] },
          { name: 'givenName', errors: [''] },
          { name: 'surname', errors: [''] },
          { name: 'phone', errors: [''] },
        ]);
      }
    }

    if (
      (bookingComplete && bookingComplete !== prevProps.condoReviewBookStore.bookingComplete) ||
      (errorBooking && errorBooking !== prevProps.condoReviewBookStore.errorBooking)
    ) {
      setTimeout(() => {
        this.props.getNavigationMenu();
      }, TEN_SEC);
    }
  }

  searchCondo = () => {
    UrlUtils.removeFromUrl(CONDO_UNITS_SESSION_KEY_LABEL);
    UrlUtils.removeFromUrl(CONDO_SESSION_KEY);

    this.props.history.push(`${Routes.CondoSearch}${location.search}`);
  };

  bookAnotherUnit = () => {
    const condoId = Number(this.props.history.location.pathname.split('/')[SECOND_ELEMENT]);

    this.props.history.push(`${Routes.Condo}/${condoId}${location.search}`);
  };

  render(): React.ReactNode {
    const { condoReviewBookStore, menuStore } = this.props;
    const { isSomeoneElse, isAccept, isAcceptKnowBeforeYouGo } = this.state;
    const {
      guest,
      condoBookingSummary,
      loadingBooking,
      bookingComplete,
      bookingErrorCode,
      isBookingPending,
      isAlternativeUnitsAvailable,
    } = condoReviewBookStore;
    const isNotification =
      bookingComplete ||
      loadingBooking ||
      isBookingPending ||
      bookingErrorCode === BookingErrorsEnum.SoldOut;

    return (
      guest && (
        <div className="condo-review-book-left-wrapper print">
          {bookingErrorCode ? this.getBookingErrorWarning() : null}
          <div className={`condo-review-book ${isNotification ? 'hide-block' : ''}`}>
            <Form name="condo-review-book-rule" ref={this.formRef}>
              <CondoGuestInfo
                formRef={this.formRef}
                onChangeSomeoneElse={this.onChangeSomeoneElse}
                isSomeoneElse={isSomeoneElse}
              />
              <CondoConfirmationEmail formRef={this.formRef} isSomeoneElse={isSomeoneElse} />
              <CondoPaymentMethod formRef={this.formRef} />
            </Form>
            <CondoCancellationPolicies condoBookingSummary={condoBookingSummary} />
            <div className="condo-review-book__wrapper">
              <Checkbox onChange={this.onChangeAccept} />
              <p className="condo-review-book__accept-text">
                <FormattedMessage
                  id="accept.you.will.be.charged"
                  values={{
                    price: renderNumber(
                      condoBookingSummary?.bookingCard?.bookingPrice?.payNowPrice,
                    ),
                    type: <FormattedMessage id="condo" />,
                  }}
                />
              </p>
            </div>
            <div className="review-book__wrapper">
              <Checkbox onChange={this.onChangeAcceptKnowBeforeYouGo} />
              <p className="review-book__accept-text">
                <FormattedMessage id="by.checking.this.box.you.acknowledge.that.you.have.read.know.before.you.go" />
              </p>
            </div>
            <div className="condo-review-book__wrapper">
              <p className="condo-review-book__agree-navigation-text">
                <FormattedMessage id="agree.booking" />
                {menuStore.items ? (
                  <a
                    className="condo-review-book__agree-navigation-link"
                    href={getFullUrl(menuStore.items?.baseUrl, PRIVACY)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FormattedMessage id="privacy.policy" />
                  </a>
                ) : null}
              </p>
              <div className="condo-review-book__complete-btn">
                <BlueButton
                  onClick={this.onCompleteBooking}
                  disabled={!isAccept || !isAcceptKnowBeforeYouGo}
                >
                  <FormattedMessage id="complete.booking" />
                </BlueButton>
              </div>
            </div>
          </div>
          {loadingBooking && <ReservationProgress isLoadingBooking={loadingBooking} />}
          <BookingConfirmed isBookingComplete={bookingComplete} />
          {isBookingPending && <CondoBookingPendingNotification />}
          {bookingErrorCode === BookingErrorsEnum.SoldOut && (
            <CondoUnitNotAvailableNotification
              isAlternativeUnitsAvailable={isAlternativeUnitsAvailable}
              searchCondos={this.searchCondo}
              bookAnotherUnit={this.bookAnotherUnit}
            />
          )}
        </div>
      )
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    condoReviewBookStore: state.condoReviewBookStore,
    menuStore: state.navigationMenuStore,
    redeemCodeStore: state.redeemCodeStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  setCondoGuest,
  getCondoBookPackageWithCard,
  setCondoPriceChangedError,
  setCondoBookingComplete,
  setErrorMessageExpirationDate,
  setErrorMessageCardNumber,
  setErrorMessageZipCode,
  getNavigationMenu,
};

export const CondoReviewBook = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(CondoReviewBookComponent));
